import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, mergeProps as _mergeProps, withCtx as _withCtx, renderList as _renderList, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, createElementBlock as _createElementBlock, withDirectives as _withDirectives, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "bg-white dark:bg-black" }

import {
  useSlots,
  onMounted,
  ref,
  nextTick,
  watch,
  computed,
  onBeforeMount,
  onUnmounted,
  unref,
} from "vue";
import { message, Table } from "ant-design-vue";
import {
  useTableMethods,
  createTableContext,
  useExportData2Excel,
  useTableForm,
  useRowForm,
  useTableState,
  useEditable,
  useColumns,
} from "./hooks";
import { ToolBar } from "./components";
import { dynamicTableProps, dynamicTableEmits } from "./dynamic-table";
import { TableActionType } from "./types";
import { SchemaForm } from "../../lt-a-form/src";
import { GuidID, getBaseUrlInfo } from "./hooks/commonMethods";
import { formatAutoCols } from "./hooks/editable-cols";
import type { RowClassName } from "ant-design-vue/lib/vc-table/interface";
import * as signalR from "@microsoft/signalr";
import ltAModel from "../../lt-a-model";
import { useScroll } from "./hooks/useScroll";
import { i18n } from "../../../locales/index";
import { isBoolean } from "@/utils/is";
import type { TableColumn } from "./types";


export default /*@__PURE__*/_defineComponent({
  __name: 'dynamic-table',
  props: dynamicTableProps,
  emits: dynamicTableEmits,
  setup(__props, { expose: __expose, emit: __emit }) {

const spinning = ref(false);
const { t } = i18n.global;

const tableID = "table-" + GuidID(); //用来识别表格实例
// console.log("生成的表格ID为：" + tableID)
const queryID = GuidID();
let fullscreenTable = ref(false); //用来控制表格是否全屏显示
const visible = ref(false);
const currentRowData = ref<Recordable>({});
const ifCaleScrollY = ref(false);
const ifDisByClient = ref(false);

let connection = ref<signalR.HubConnection | null>(null);
let currentKey = ref(-1);
let lastCurrentKey = ref(-1);
const editFormRef = ref(null);

const props = __props;
const emit = __emit;
const slots = useSlots();
const currentRowKeyValue = ref("");
const tableState = useTableState({ props, slots, tableID, emit });

const {
  tableRef,
  tableData,
  queryFormRef,
  editTableFormRef,
  getBindValues,
  editFormModel,
} = tableState;
// 表格内部方法

const editableHooks = useEditable({ props, state: tableState });
const tableMethods = useTableMethods({
  state: tableState,
  props,
  emit,
  lastCurrentKey,
  currentKey,
  visible,
  currentRowData,
  spinning,
  currentRowKeyValue,
  tableID,
  editableHooks,
  connection,
  ifDisByClient,
});
const {
  setProps,
  getTableData,
  handleSubmit,
  reload,
  handleTableChange,
  handleResizeColumn,
  getOrderParama,
  orderChangeByForm,
  getDataByMonitor,
  startConect,
  setCellTtitle,
  initColumnFilter,
  getComplexCondition,
  rowClick,
  headerClick,
  cellClick,
  headerCellClick,
  editRowDlgOnOK,
  fetchData,
  queryGroupData,
} = tableMethods;

const localcustomRow = computed(() => {
  return props.customRow ? props.customRow : handleRowClick;
});

const tableAction: TableActionType = {
  setProps,
  reload,
  getTableData,
  ...editableHooks,
};

const editDlgWidth = computed(() => {
  return props.editDlgWidth ? props.editDlgWidth : "800px";
});

const editDlgHeight = computed(() => {
  return props.editDlgHeight ? props.editDlgHeight : "600px";
});

const showCols = computed(() => {
  // 合并 innerProps，使 setProps 更新的列宽对所有模式生效
  const columns = unref(tableState.getProps).columns ?? props.columns;
  //兼容外部列功能
  if (props.isUseOutCols == true) {
    return columns;
  }
  if (props.isAutoEditable == true) {
    const cols = columns;
    if (props?.showIndex) {
      const indexCols = cols.filter((item) => {
        return item.dataIndex == "INDEX";
      });
      if (indexCols.length == 0) {
        cols.unshift({
          dataIndex: "INDEX",
          title: t("component.table.index"),
          width: 60,
          align: "center",
          fixed: "left",
          Visible: true,
          Style: "INDEX",
          customRender: ({ index }) => {
            const getPagination = unref(tableState.paginationRef);
            if (isBoolean(getPagination)) {
              return index + 1;
            }
            const { current = 1, pageSize = 10 } = getPagination!;
            return ((current < 1 ? 1 : current) - 1) * pageSize + index + 1;
          },
        } as TableColumn);
      }
    }
    const tempCols = formatAutoCols(
      props,
      emit,
      tableState,
      cols,
      spinning,
      tableAction,
    );

    return tempCols;
  } else {
    return innerColumns.value;
  }
});

// 表格列的配置描述
const { innerColumns } = useColumns({
  props,
  slots,
  state: tableState,
  methods: tableMethods,
  tableAction,
  emit,
  tableID,
  spinning,
});

const showOrderParama = computed(() => {
  //联合排序功能，只用于后台排序，本地排序不做处理
  return props.OrderParam.length > 0 && props.dataSource?.length == 0;
});

const isExportCurrent = computed(() => {
  //联合排序功能，只用于后台排序，本地排序不做处理
  return typeof props.url == "undefined";
});
// 搜索表单
const { getFormProps, replaceFormSlotKey, getFormSlotKeys } = useTableForm({
  tableState,
  tableMethods,
  slots,
});

// 查看详细信息表单
const { getRowFormProps, replaceRowSlotKey, getRowSlotKeys } = useRowForm({
  tableState,
  tableMethods,
  slots,
});

// 表单导出
const exportData2ExcelHooks = useExportData2Excel({
  props,
  state: tableState,
  methods: tableMethods,
});

const { scroll } = useScroll({
  props,
  tableID,
  tableData,
  tableMethods,
  tableState,
  ifCaleScrollY,
});

// 当前组件所有的状态和方法
const instance = {
  ...props,
  ...tableState,
  ...tableMethods,
  ...editableHooks,
  ...exportData2ExcelHooks,
};

createTableContext(instance);
__expose(instance);

const getContainer = function () {
  return document.body;
};
//行点击事件
const handleRowClick = function (record, index) {
  return rowClick(record, index, currentKey);
};

const handleColsChange = function (cols) {
  // console.log("执行列改变事件")
  initColumnFilter(tableID, props, cols);
};

const handleOrderChange = function (value, isUnion) {
  orderChangeByForm(value, isUnion);
  const sendData = {
    value: value,
    isUnion: isUnion,
  };
  //像外部推送排序详细信息
  emit("order-change", sendData);
};

const handleFullScreen = function (value) {
  fullscreenTable.value = value;
};

const handleComplexQuery = function (value) {
  getComplexCondition(value);
};

const setRowClassName = function (record, index): string | RowClassName<any> {
  //是否高亮显示当前行
  if (props.highlightCurrentRow && index == currentKey.value) {
    return "curren-row";
  }
  if (props.stripe) {
    if (index % 2 === 1) {
      return "table-striped";
    }
  } else {
    return "";
  }
  return "";
};

watch(
  [() => props.dataSource],
  () => {
    if (props.dataSource) {
      fetchData();
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  () => props.queryParams,
  () => {
    getTableData();
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  () => tableData.value,
  () => {
    //console.log("当前展示数据是：" + JSON.stringify(tableData.value))
  },
);

const onOk = () => {
  if (editFormRef.value) {
    //@ts-ignore
    //借用底层函数实现form表单数据
    editFormRef.value.submit();
  }
  // visible.value = false
};

const handleEditRowSubmit = (value) => {
  editRowDlgOnOK(value);
  if (!props.editDlgCloseByOut) {
    visible.value = false;
  }
};

const handleError = () => {
  message.warn(t("component.table.validWarning"));
  visible.value;
};

const handleOnClose = function () {
  visible.value = false;
};

watch(
  () => [props.setCurrentNum, showCols.value],
  (val, oldVal) => {
    if (props.setCurrentNum != -1) {
      //设置当前行关键字
      currentKey.value = props.setCurrentNum;
      //@ts-ignore
      lastCurrentKey.value = oldVal[0];
    }
  },
);

watch(
  () => lastCurrentKey.value,
  (val, oldVal) => {
    console.log("当前行关键字改变了：" + val);
  },
);

watch(
  () => props.selectTypeAfterload,
  (val, oldVal) => {
    if (props.selectTypeAfterload == 1) {
      currentKey.value = 0;
      lastCurrentKey.value = currentKey.value;
    } else if (props.selectTypeAfterload == 2) {
      currentKey.value = lastCurrentKey.value;
      lastCurrentKey.value = currentKey.value;
    }
  },
);

watch(
  () => [props.dataName, props.url],
  () => {
    //20250715为低代码增加的功能，常规业务中，意义不大
    getTableData();
  },
);

const localSingnalrUrl = computed(() => {
  if (props.signalrUrl) {
    return props.signalrUrl;
  } else {
    return getBaseUrlInfo() + "/notify";
  }
});

if (props.ifMonitor) {
  //@ts-ignore
  connection.value = new signalR.HubConnectionBuilder() //服务器地址
    .withUrl(localSingnalrUrl.value, {})
    .configureLogging(signalR.LogLevel.Information)
    .build();
}

onBeforeMount(() => {
  if (props.ifMonitor) {
    getDataByMonitor(queryID);
  }
});

onMounted(() => {
  getOrderParama();
  nextTick(() => {
    getTableData();
    if (props.ifFixed) setCellTtitle(tableID);
  });
  if (props.ifMonitor) {
    startConect();
    if (connection.value) {
      connection.value.onclose(async () => {
        if (ifDisByClient.value == false) {
          console.log("尝试重新连接");
          await startConect();
        }
      });
    }
  }
  //
  //getLocalShowCols()
});

onUnmounted(() => {
  if (props.ifMonitor) {
    if (connection.value) {
      connection.value.send("UnSubcribe", queryGroupData.ScruSubcribeId);
      connection.value.stop();
      connection.value = null;
    }
  }
});

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", {
    style: {"height":"100%"},
    id: 'tableparent-' + tableID
  }, [
    (_openBlock(), _createBlock(_Teleport, {
      to: getContainer(),
      disabled: _unref(fullscreenTable) == false
    }, [
      _withDirectives((_openBlock(), _createElementBlock("div", {
        style: {"height":"100%"},
        class: _normalizeClass(["custom-table", { fullscreen: _unref(fullscreenTable) == true }]),
        ref: "reftableDiv",
        id: tableID
      }, [
        (_ctx.searchBox)
          ? (_openBlock(), _createBlock(_unref(SchemaForm), _mergeProps({
              key: 0,
              ref_key: "queryFormRef",
              ref: queryFormRef,
              class: "bg-white dark:bg-black mb-16px pt-24px pr-24px table-search-form",
              "submit-on-reset": ""
            }, _unref(getFormProps), {
              "is-outline": false,
              "table-instance": tableAction,
              onToggleAdvanced: _cache[0] || (_cache[0] = (e) => _ctx.$emit('toggle-advanced', e)),
              onSubmit: _unref(handleSubmit)
            }), _createSlots({ _: 2 }, [
              _renderList(_unref(getFormSlotKeys), (item) => {
                return {
                  name: _unref(replaceFormSlotKey)(item),
                  fn: _withCtx((data) => [
                    _renderSlot(_ctx.$slots, item, _normalizeProps(_guardReactiveProps(data || {})))
                  ])
                }
              })
            ]), 1040, ["onSubmit"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          (_ctx.showToolBar)
            ? (_openBlock(), _createBlock(_unref(ToolBar), {
                key: 0,
                title: _ctx.headerTitle,
                "title-tooltip": _ctx.titleTooltip,
                "show-table-setting": _ctx.showTableSetting,
                showOrderParama: showOrderParama.value,
                showFullScreen: _unref(fullscreenTable),
                showAutoForm: props.queryFilterForm,
                "export-file-name": _ctx.exportFileName,
                "is-export-current": isExportCurrent.value,
                class: "table-tool-bar",
                onColsChange: handleColsChange,
                onOrderChange: handleOrderChange,
                onFullScreen: handleFullScreen,
                onComplexQuery: handleComplexQuery,
                style: {"height":"26px"}
              }, _createSlots({ _: 2 }, [
                _renderList(Object.keys(_ctx.$slots), (name) => {
                  return {
                    name: name,
                    fn: _withCtx((data) => [
                      _renderSlot(_ctx.$slots, name, _normalizeProps(_guardReactiveProps(data || {})))
                    ])
                  }
                })
              ]), 1032, ["title", "title-tooltip", "show-table-setting", "showOrderParama", "showFullScreen", "showAutoForm", "export-file-name", "is-export-current"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(SchemaForm), {
            ref_key: "editTableFormRef",
            ref: editTableFormRef,
            "no-style": "",
            "initial-values": _unref(editFormModel),
            "show-action-button-group": false,
            "show-advanced-button": false,
            style: _normalizeStyle({ height: _ctx.showToolBar ? 'calc(100% - 26px)' : '100%' })
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Table), _mergeProps({
                class: "base-table",
                ref_key: "tableRef",
                ref: tableRef,
                id: tableID
              }, _unref(getBindValues), {
                columns: showCols.value,
                "data-source": _unref(tableData),
                scroll: unref(_unref(scroll)),
                "row-class-name": 
                props.rowClassName ? props.rowClassName : setRowClassName
              ,
                onResizeColumn: _unref(handleResizeColumn),
                customRow: localcustomRow.value,
                customHeaderRow: _unref(headerClick),
                customCell: _unref(cellClick),
                customHeaderCell: _unref(headerCellClick),
                onChange: _unref(handleTableChange)
              }), _createSlots({
                bodyCell: _withCtx((slotData) => [
                  _renderSlot(_ctx.$slots, "bodyCell", _normalizeProps(_guardReactiveProps(slotData)))
                ]),
                _: 2
              }, [
                _renderList(_ctx.$slots, (_, slotName) => {
                  return {
                    name: slotName,
                    fn: _withCtx((slotData) => [
                      _renderSlot(_ctx.$slots, slotName, _normalizeProps(_guardReactiveProps(slotData)))
                    ])
                  }
                })
              ]), 1040, ["columns", "data-source", "scroll", "row-class-name", "onResizeColumn", "customRow", "customHeaderRow", "customCell", "customHeaderCell", "onChange"])
            ]),
            _: 3
          }, 8, ["initial-values", "style"])
        ])
      ], 2)), [
        [_directive_loading, spinning.value]
      ]),
      _createVNode(_unref(ltAModel), {
        open: visible.value,
        "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => ((visible).value = $event)),
        onOk: onOk,
        title: _ctx.$t('component.table.showInDlg'),
        afterClose: handleOnClose,
        width: editDlgWidth.value
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            style: _normalizeStyle({ height: editDlgHeight.value, width: '100%' })
          }, [
            _createVNode(_unref(SchemaForm), _mergeProps({
              ref_key: "editFormRef",
              ref: editFormRef,
              class: "bg-white dark:bg-black mb-16px pt-24px pr-24px table-edit-row-form",
              "submit-on-reset": ""
            }, _unref(getRowFormProps), {
              "initial-values": currentRowData.value,
              "table-instance": tableAction,
              onSubmit: handleEditRowSubmit,
              onSubmitError: handleError
            }), _createSlots({ _: 2 }, [
              _renderList(_unref(getRowSlotKeys), (item) => {
                return {
                  name: _unref(replaceRowSlotKey)(item),
                  fn: _withCtx((data) => [
                    _renderSlot(_ctx.$slots, item, _normalizeProps(_guardReactiveProps(data || {})))
                  ])
                }
              })
            ]), 1040, ["initial-values"])
          ], 4)
        ]),
        _: 3
      }, 8, ["open", "title", "width"])
    ], 8, ["to", "disabled"]))
  ], 8, _hoisted_1))
}
}

})